"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter, usePathname } from "next/navigation";

// Create a globally available function
declare global {
  interface Window {
    navigateToContact: () => void;
  }
}

export default function ContactNavigator() {
  const router = useRouter();
  const pathname = usePathname();

  // Set up a state to track if we just navigated to the homepage
  const [justNavigated, setJustNavigated] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const MAX_ATTEMPTS = 10;

  // Function to perform the actual scrolling
  const scrollToContactSection = useCallback(() => {
    const contactSection = document.getElementById("contact-us");
    if (contactSection) {
      // Try both methods of scrolling
      try {
        contactSection.scrollIntoView({ behavior: "smooth" });
      } catch (e) {
        // Fallback
        window.scrollTo({
          top: contactSection.offsetTop - 80,
          behavior: "smooth",
        });
      }

      // Also try direct positioning as a fallback
      setTimeout(() => {
        window.scrollTo(0, contactSection.offsetTop - 80);
      }, 100);

      return true;
    }
    return false;
  }, []);

  // Function to be called from anywhere in the app
  const navigateToContact = useCallback(() => {
    // If we're on the homepage
    if (pathname === "/" || pathname === "") {
      // Try to scroll immediately
      const success = scrollToContactSection();

      if (!success) {
        // If not successful, set up recurring attempts
        setJustNavigated(true);
        setAttemptCount(0);
      }
    } else {
      // We're not on homepage, navigate there with hash
      router.push("/#contact-us");
      setJustNavigated(true);
      setAttemptCount(0);
    }
  }, [pathname, router, scrollToContactSection]);

  // Effect to handle post-navigation scrolling
  useEffect(() => {
    // Skip if we didn't just navigate
    if (!justNavigated) return;

    // Check for the hash in URL
    const hasContactHash = window.location.hash === "#contact-us";

    // Try to scroll
    const attemptScroll = () => {
      const success = scrollToContactSection();

      if (success) {
        // If successful, reset state
        setJustNavigated(false);
        setAttemptCount(0);
      } else {
        // If not successful and we haven't reached max attempts, try again
        if (attemptCount < MAX_ATTEMPTS) {
          setAttemptCount((prev) => prev + 1);
          setTimeout(attemptScroll, 300);
        } else {
          // Give up after max attempts
          setJustNavigated(false);
          setAttemptCount(0);
        }
      }
    };

    // Start attempting to scroll
    setTimeout(attemptScroll, 300);

    // Cleanup
    return () => {
      setJustNavigated(false);
      setAttemptCount(0);
    };
  }, [justNavigated, attemptCount, scrollToContactSection]);

  // Register the global navigation function
  useEffect(() => {
    window.navigateToContact = navigateToContact;

    return () => {
      // Clean up
      window.navigateToContact = () => {};
    };
  }, [navigateToContact]);

  // This is a utility component, it doesn't render anything
  return null;
}
